import type { InsightsAssistantKind, InsightsChatItem, InsightsToolActivity } from "../types";

const TASK_TOOLS = new Set(["task_write", "task_update", "task_complete", "task_check"]);

export function isInsightsTaskToolName(tool: string): boolean {
  return TASK_TOOLS.has(tool);
}

export type SessionMessage = {
  id?: string;
  role: string;
  content: string;
  createdAt?: string;
  investigationId?: string;
  partId?: string;
  assistantKind?: InsightsAssistantKind;
};

export type SessionEvent = {
  event: string;
  data: unknown;
  seq?: number;
};

function newId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function closeStreamingAssistants(items: InsightsChatItem[]): InsightsChatItem[] {
  return items.map((item) =>
    item.kind === "assistant" && item.streaming ? { ...item, streaming: false } : item,
  );
}

/**
 * Rebuild transcript from the SSE event buffer (same order as a live run).
 * Falls back to messages + toolActivity when events are missing.
 */
export function buildHydratedItems(
  messages: SessionMessage[],
  toolActivity: InsightsToolActivity[],
  streamingAssistantText?: string | null,
  events?: SessionEvent[] | null,
): InsightsChatItem[] {
  if (events && events.length > 0) {
    const hasToolEvents = events.some(
      (ev) => ev.event === "tool-call" || ev.event === "tool-result",
    );
    // Ring buffer often retains only late text-deltas after a long answer —
    // prefer messages + toolActivity so we keep tools and complete <render> blocks.
    if (!hasToolEvents && (messages.length > 0 || toolActivity.length > 0)) {
      return buildItemsFromMessagesAndTools(messages, toolActivity, streamingAssistantText);
    }
    const fromEvents = buildItemsFromEvents(messages, events, streamingAssistantText);
    // Event trim can drop pre-task tools; re-attach untagged rows from toolActivity
    // so the provisional "Start Investigation" panel survives refresh.
    const repaired = repairTruncatedAssistantText(fromEvents, messages);
    return mergeMissingProvisionalTools(repaired, toolActivity);
  }
  return buildItemsFromMessagesAndTools(messages, toolActivity, streamingAssistantText);
}

/**
 * Insert toolActivity rows that lack taskId and are missing from event replay.
 * Only when the buffer kept stamped tools but dropped the pre-task era
 * (Start Investigation). Callers: buildHydratedItems after event hydrate.
 */
export function mergeMissingProvisionalTools(
  items: InsightsChatItem[],
  toolActivity: InsightsToolActivity[],
): InsightsChatItem[] {
  const eventTools = items.filter((i) => i.kind === "tool");
  const hasTasked = eventTools.some((i) => Boolean(i.activity.taskId));
  const hasUntagged = eventTools.some((i) => !i.activity.taskId);
  // Events still include the provisional era, or never reached tasks — nothing to repair.
  if (!hasTasked || hasUntagged) return items;

  const presentIds = new Set(eventTools.map((i) => i.id));
  const missing = toolActivity.filter(
    (a) => !presentIds.has(a.id) && !a.taskId && !isInsightsTaskToolName(a.tool),
  );
  if (!missing.length) return items;

  const toolItems: InsightsChatItem[] = missing.map((a) => ({
    id: a.id || newId(),
    kind: "tool",
    activity: a,
  }));

  const firstUserIdx = items.findIndex((i) => i.kind === "user");
  const insertAt = firstUserIdx >= 0 ? firstUserIdx + 1 : 0;
  return [...items.slice(0, insertAt), ...toolItems, ...items.slice(insertAt)];
}

/** Event ring buffer can drop the start of a long answer (orphan </render>, mid-tag). */
export function isTruncatedAssistantText(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  const hasClose = /<\/render>/i.test(trimmed);
  const hasOpen = /<render\b/i.test(trimmed);
  if (hasClose && !hasOpen) return true;
  // Truncation mid-attribute, e.g. `0 .85rem 0;color:…">…</h3>`
  if (hasClose && /^[\d.]/.test(trimmed) && !trimmed.startsWith("<")) return true;
  return false;
}

/**
 * Prefer host message id/partId for complete content. Tag-soup fuzzy repair is
 * legacy only when the item has no matching persisted message.
 */
export function repairTruncatedAssistantText(
  items: InsightsChatItem[],
  messages: SessionMessage[],
): InsightsChatItem[] {
  const byId = new Map<string, SessionMessage>();
  for (const m of messages) {
    if (m.role !== "assistant" || !m.content.trim()) continue;
    if (m.id) byId.set(m.id, m);
    if (m.partId) byId.set(m.partId, m);
  }
  if (!byId.size && !messages.some((m) => m.role === "assistant")) return items;

  return items.map((item) => {
    if (item.kind !== "assistant") return item;

    const keyed =
      (item.partId && byId.get(item.partId)) || (item.id && byId.get(item.id)) || undefined;
    if (keyed) {
      const complete = !isTruncatedAssistantText(keyed.content);
      if (complete || keyed.content.length >= item.text.length) {
        return {
          ...item,
          id: keyed.id || item.id,
          partId: keyed.partId || item.partId || keyed.id,
          text: keyed.content,
          streaming: false,
          ...(keyed.assistantKind ? { assistantKind: keyed.assistantKind } : {}),
        };
      }
    }

    // Legacy fuzzy only for orphan event bubbles without a matching message id.
    if (!isTruncatedAssistantText(item.text)) {
      return {
        ...item,
        ...(keyed?.assistantKind ? { assistantKind: keyed.assistantKind } : {}),
      };
    }

    const assistantContents = messages
      .filter((m) => m.role === "assistant" && m.content.trim())
      .map((m) => m.content);
    const fragment = item.text;
    const match =
      assistantContents.find(
        (c) =>
          c.length > fragment.length &&
          (c.endsWith(fragment) || c.includes(fragment)) &&
          /<render\b/i.test(c),
      ) ||
      assistantContents
        .filter((c) => /<render\b/i.test(c) && /<\/render>/i.test(c))
        .sort((a, b) => b.length - a.length)[0];

    if (!match) return item;
    return { ...item, text: match, streaming: false };
  });
}

function buildItemsFromEvents(
  messages: SessionMessage[],
  events: SessionEvent[],
  streamingAssistantText?: string | null,
): InsightsChatItem[] {
  let items: InsightsChatItem[] = [];
  const hasUserEvents = events.some((ev) => ev.event === "user-message");

  if (!hasUserEvents) {
    for (const m of messages) {
      if (m.role === "user") {
        items.push({
          id: m.id || newId(),
          kind: "user",
          text: m.content,
          ...(m.investigationId ? { investigationId: m.investigationId } : {}),
        });
      }
    }
  }

  for (const ev of events) {
    const d = (ev.data ?? {}) as Record<string, unknown>;
    if (ev.event === "user-message" && typeof d.content === "string") {
      items = closeStreamingAssistants(items);
      items.push({
        id: typeof d.id === "string" ? d.id : newId(),
        kind: "user",
        text: d.content,
        ...(typeof d.investigationId === "string" ? { investigationId: d.investigationId } : {}),
      });
      continue;
    }
    if (ev.event === "text-delta" && typeof d.text === "string") {
      const partId = typeof d.partId === "string" ? d.partId : undefined;
      const assistantKind =
        typeof d.assistantKind === "string"
          ? (d.assistantKind as InsightsAssistantKind)
          : undefined;
      const nextText = typeof d.fullText === "string" ? d.fullText : null;
      const last = items[items.length - 1];
      if (last?.kind === "assistant" && last.streaming) {
        items = [
          ...items.slice(0, -1),
          {
            ...last,
            text: nextText ?? last.text + d.text,
            ...(partId ? { partId, id: partId } : {}),
            ...(assistantKind ? { assistantKind } : {}),
          },
        ];
      } else {
        items = [
          ...items,
          {
            id: partId || newId(),
            kind: "assistant",
            text: nextText ?? d.text,
            streaming: true,
            ...(partId ? { partId } : {}),
            ...(assistantKind ? { assistantKind } : {}),
          },
        ];
      }
      continue;
    }
    if (ev.event === "assistant-message" || ev.event === "final-answer") {
      const partId =
        (typeof d.partId === "string" && d.partId) ||
        (typeof d.id === "string" && d.id) ||
        undefined;
      const fullText = typeof d.text === "string" ? d.text : "";
      const assistantKind =
        typeof d.assistantKind === "string"
          ? (d.assistantKind as InsightsAssistantKind)
          : ev.event === "final-answer"
            ? ("final_answer" as const)
            : undefined;
      if (partId) {
        const idx = items.findIndex(
          (it) => it.kind === "assistant" && (it.partId === partId || it.id === partId),
        );
        if (idx >= 0) {
          const cur = items[idx]!;
          if (cur.kind === "assistant") {
            items = [...items];
            items[idx] = {
              ...cur,
              id: partId,
              partId,
              text: fullText || cur.text,
              streaming: false,
              ...(assistantKind ? { assistantKind } : {}),
            };
            continue;
          }
        }
      }
      if (fullText) {
        items = [
          ...closeStreamingAssistants(items),
          {
            id: partId || newId(),
            kind: "assistant",
            text: fullText,
            streaming: false,
            ...(partId ? { partId } : {}),
            ...(assistantKind ? { assistantKind } : {}),
          },
        ];
      }
      continue;
    }
    if (ev.event === "tool-call") {
      const tool = String(d.tool ?? "tool");
      if (isInsightsTaskToolName(tool)) continue;
      const activity: InsightsToolActivity = {
        id: (d.eventId as string) || newId(),
        tool,
        phase: "call",
        args: d.args,
        ...(typeof d.taskId === "string" ? { taskId: d.taskId } : {}),
        ...(typeof d.toolCallId === "string" ? { toolCallId: d.toolCallId } : {}),
        ...(typeof d.startedAt === "string" ? { startedAt: d.startedAt } : {}),
      };
      items = [...closeStreamingAssistants(items), { id: activity.id, kind: "tool", activity }];
      continue;
    }
    if (ev.event === "tool-result") {
      const tool = String(d.tool ?? "tool");
      if (isInsightsTaskToolName(tool)) continue;
      const activity: InsightsToolActivity = {
        id: (d.eventId as string) || newId(),
        tool,
        phase: "result",
        result: d.result,
        error: Boolean(d.error),
        softRefuse: Boolean(d.softRefuse),
        ...(typeof d.taskId === "string" ? { taskId: d.taskId } : {}),
        ...(typeof d.toolCallId === "string" ? { toolCallId: d.toolCallId } : {}),
        ...(typeof d.startedAt === "string" ? { startedAt: d.startedAt } : {}),
        ...(typeof d.endedAt === "string" ? { endedAt: d.endedAt } : {}),
        ...(typeof d.durationMs === "number" ? { durationMs: d.durationMs } : {}),
      };
      items = [...items, { id: activity.id, kind: "tool", activity }];
    }
  }

  if (streamingAssistantText?.trim()) {
    const last = items[items.length - 1];
    if (last?.kind === "assistant" && last.streaming) {
      // Event replay already has the live buffer; prefer explicit snapshot text if longer.
      if (streamingAssistantText.length >= last.text.length) {
        items = [...items.slice(0, -1), { ...last, text: streamingAssistantText, streaming: true }];
      }
    } else {
      items = [
        ...closeStreamingAssistants(items),
        {
          id: newId(),
          kind: "assistant",
          text: streamingAssistantText,
          streaming: true,
        },
      ];
    }
  } else {
    items = closeStreamingAssistants(items);
  }

  return items;
}

function buildItemsFromMessagesAndTools(
  messages: SessionMessage[],
  toolActivity: InsightsToolActivity[],
  streamingAssistantText?: string | null,
): InsightsChatItem[] {
  type Row = { t: string; item: InsightsChatItem };
  const rows: Row[] = [];

  for (const m of messages) {
    const kind = m.role === "user" ? ("user" as const) : ("assistant" as const);
    rows.push({
      t: m.createdAt ?? "",
      item:
        kind === "user"
          ? {
              id: m.id || newId(),
              kind,
              text: m.content,
              ...(m.investigationId ? { investigationId: m.investigationId } : {}),
            }
          : {
              id: m.id || m.partId || newId(),
              kind,
              text: m.content,
              ...(m.partId ? { partId: m.partId } : {}),
              ...(m.assistantKind ? { assistantKind: m.assistantKind } : {}),
            },
    });
  }
  for (const a of toolActivity) {
    if (isInsightsTaskToolName(a.tool)) continue;
    rows.push({
      t: a.createdAt ?? "",
      item: { id: a.id || newId(), kind: "tool", activity: a },
    });
  }
  rows.sort((a, b) => a.t.localeCompare(b.t));
  const items = rows.map((r) => r.item);
  if (streamingAssistantText?.trim()) {
    items.push({
      id: newId(),
      kind: "assistant",
      text: streamingAssistantText,
      streaming: true,
    });
  }
  return items;
}

/**
 * After refresh, Mastra/session messages often lack investigationId. Stamp user
 * openers by matching text to investigation summaries (header opener + GET hydrate).
 */
export function stampInvestigationIdsFromSummaries(
  items: InsightsChatItem[],
  investigations: Array<{ investigationId: string; question: string }>,
): InsightsChatItem[] {
  if (!items.length || !investigations.length) return items;
  const byQuestion = new Map<string, string>();
  for (const inv of investigations) {
    const q = inv.question.trim();
    if (q) byQuestion.set(q, inv.investigationId);
  }
  if (!byQuestion.size) return items;
  return items.map((item) => {
    if (item.kind !== "user" || item.investigationId) return item;
    const id = byQuestion.get(item.text.trim());
    return id ? { ...item, investigationId: id } : item;
  });
}
